import { EmailConfirmationData } from '@/types/signature';

export class EmailService {
  // Enviar email de confirmação de assinatura
  static async sendConfirmationEmail(data: EmailConfirmationData): Promise<void> {
    try {
      const response = await fetch('/api/email/send-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Falha ao enviar email de confirmação');
      }

      console.log('Email enviado com sucesso');
    } catch (error) {
      console.error('Erro no serviço de email:', error);
      throw error;
    }
  }

  // Enviar cópia do documento assinado
  static async sendSignedDocumentCopy(
    email: string,
    nomeAssinante: string,
    tituloDocumento: string,
    pdfUrl: string
  ): Promise<void> {
    try {
      const response = await fetch('/api/email/send-signed-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          nomeAssinante,
          tituloDocumento,
          pdfUrl,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Falha ao enviar cópia do documento');
      }

      console.log('Cópia do documento enviada com sucesso');
    } catch (error) {
      console.error('Erro ao enviar cópia do documento:', error);
      throw error;
    }
  }

  // Gerar HTML do email de confirmação
  static generateConfirmationEmailHTML(data: EmailConfirmationData): string {
    return `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Confirmação de Assinatura</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            background-color: #1e40af;
            color: white;
            padding: 20px;
            text-align: center;
            border-radius: 8px 8px 0 0;
          }
          .content {
            background-color: #f8fafc;
            padding: 30px;
            border-radius: 0 0 8px 8px;
          }
          .button {
            display: inline-block;
            background-color: #10b981;
            color: white;
            padding: 12px 24px;
            text-decoration: none;
            border-radius: 6px;
            margin: 20px 0;
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            font-size: 12px;
            color: #6b7280;
          }
          .warning {
            background-color: #fef3c7;
            border: 1px solid #f59e0b;
            padding: 15px;
            border-radius: 6px;
            margin: 20px 0;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🔐 Confirmação de Assinatura</h1>
          <p>Juris Hub Nexus - Sistema de Assinatura Eletrônica</p>
        </div>
        
        <div class="content">
          <h2>Olá, ${data.nome_assinante}!</h2>
          
          <p>Você assinou eletronicamente o documento:</p>
          <h3>"${data.titulo_documento}"</h3>
          
          <div class="warning">
            <strong>⚠️ Confirmação Necessária</strong><br>
            Para finalizar o processo de assinatura, você precisa confirmar sua identidade clicando no botão abaixo.
          </div>
          
          <p>Esta confirmação é necessária para garantir a validade jurídica da assinatura eletrônica, conforme a legislação brasileira.</p>
          
          <div style="text-align: center;">
            <a href="${data.link_confirmacao}" class="button">
              ✅ Confirmar Assinatura
            </a>
          </div>
          
          <p><strong>Informações importantes:</strong></p>
          <ul>
            <li>Esta confirmação é obrigatória para validar a assinatura</li>
            <li>O link expira em 24 horas por questões de segurança</li>
            <li>Após a confirmação, você receberá uma cópia do documento assinado</li>
            <li>O documento será marcado como oficialmente assinado</li>
          </ul>
          
          <p>Se você não solicitou esta assinatura, ignore este email.</p>
        </div>
        
        <div class="footer">
          <p><strong>Juris Hub Nexus</strong></p>
          <p>Sistema de Assinatura Eletrônica Jurídica</p>
          <p>Conforme MP 2.200-2/2001 e Lei 14.063/2020</p>
          <p>Este é um email automático, não responda a esta mensagem.</p>
        </div>
      </body>
      </html>
    `;
  }

  // Gerar texto do email de confirmação
  static generateConfirmationEmailText(data: EmailConfirmationData): string {
    return `
CONFIRMAÇÃO DE ASSINATURA ELETRÔNICA

Olá, ${data.nome_assinante}!

Você assinou eletronicamente o documento: "${data.titulo_documento}"

⚠️ CONFIRMAÇÃO NECESSÁRIA
Para finalizar o processo de assinatura, você precisa confirmar sua identidade.

Link de confirmação: ${data.link_confirmacao}

INFORMAÇÕES IMPORTANTES:
- Esta confirmação é obrigatória para validar a assinatura
- O link expira em 24 horas por questões de segurança
- Após a confirmação, você receberá uma cópia do documento assinado
- O documento será marcado como oficialmente assinado

Se você não solicitou esta assinatura, ignore este email.

---
Juris Hub Nexus
Sistema de Assinatura Eletrônica Jurídica
Conforme MP 2.200-2/2001 e Lei 14.063/2020
    `;
  }

  // Gerar HTML do email com cópia do documento
  static generateSignedDocumentEmailHTML(
    nomeAssinante: string,
    tituloDocumento: string,
    pdfUrl: string
  ): string {
    return `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Documento Assinado</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            background-color: #059669;
            color: white;
            padding: 20px;
            text-align: center;
            border-radius: 8px 8px 0 0;
          }
          .content {
            background-color: #f8fafc;
            padding: 30px;
            border-radius: 0 0 8px 8px;
          }
          .button {
            display: inline-block;
            background-color: #1e40af;
            color: white;
            padding: 12px 24px;
            text-decoration: none;
            border-radius: 6px;
            margin: 20px 0;
            font-weight: bold;
          }
          .success {
            background-color: #d1fae5;
            border: 1px solid #10b981;
            padding: 15px;
            border-radius: 6px;
            margin: 20px 0;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            font-size: 12px;
            color: #6b7280;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>✅ Documento Assinado com Sucesso</h1>
          <p>Juris Hub Nexus - Sistema de Assinatura Eletrônica</p>
        </div>
        
        <div class="content">
          <h2>Olá, ${nomeAssinante}!</h2>
          
          <div class="success">
            <strong>✅ Assinatura Confirmada</strong><br>
            Seu documento foi oficialmente assinado e é válido juridicamente.
          </div>
          
          <p>O documento <strong>"${tituloDocumento}"</strong> foi assinado eletronicamente e está disponível para download.</p>
          
          <div style="text-align: center;">
            <a href="${pdfUrl}" class="button">
              📄 Baixar Documento Assinado
            </a>
          </div>
          
          <p><strong>Características do documento assinado:</strong></p>
          <ul>
            <li>✅ Assinatura eletrônica válida juridicamente</li>
            <li>🔒 Hash SHA256 para verificação de integridade</li>
            <li>📱 QR Code para verificação online</li>
            <li>📸 Foto do assinante capturada</li>
            <li>🖊️ Assinatura digital capturada</li>
            <li>📅 Data e hora da assinatura registradas</li>
            <li>🌐 IP e dispositivo registrados</li>
          </ul>
          
          <p><strong>Para verificar a autenticidade:</strong></p>
          <p>O documento contém um QR Code que pode ser escaneado para verificação online da validade da assinatura.</p>
        </div>
        
        <div class="footer">
          <p><strong>Juris Hub Nexus</strong></p>
          <p>Sistema de Assinatura Eletrônica Jurídica</p>
          <p>Conforme MP 2.200-2/2001 e Lei 14.063/2020</p>
          <p>Este é um email automático, não responda a esta mensagem.</p>
        </div>
      </body>
      </html>
    `;
  }

  // Gerar texto do email com cópia do documento
  static generateSignedDocumentEmailText(
    nomeAssinante: string,
    tituloDocumento: string,
    pdfUrl: string
  ): string {
    return `
DOCUMENTO ASSINADO COM SUCESSO

Olá, ${nomeAssinante}!

✅ ASSINATURA CONFIRMADA
Seu documento foi oficialmente assinado e é válido juridicamente.

O documento "${tituloDocumento}" foi assinado eletronicamente e está disponível para download.

Link para download: ${pdfUrl}

CARACTERÍSTICAS DO DOCUMENTO ASSINADO:
- ✅ Assinatura eletrônica válida juridicamente
- 🔒 Hash SHA256 para verificação de integridade
- 📱 QR Code para verificação online
- 📸 Foto do assinante capturada
- 🖊️ Assinatura digital capturada
- 📅 Data e hora da assinatura registradas
- 🌐 IP e dispositivo registrados

Para verificar a autenticidade:
O documento contém um QR Code que pode ser escaneado para verificação online da validade da assinatura.

---
Juris Hub Nexus
Sistema de Assinatura Eletrônica Jurídica
Conforme MP 2.200-2/2001 e Lei 14.063/2020
    `;
  }
}
